<script lang="ts" setup>
import { ref, watch, onMounted, onUnmounted, computed } from "vue";
import { useI18n } from 'vue-i18n';
import { ElNotification } from 'element-plus';
import iconv from 'iconv-lite';
import { DeleteOutlined } from '@xpos/icons-svg';
import NoteQuickClick from "./NoteQuickClick.vue";
import { defineStorageStore, RemarkItem } from '@/utils/storage-helper';
import { useConfigStore } from '@/stores/config';
import { logError, serializeError } from '@/utils/logger';

type ModelType = 'invoice_line_remark' | 'invoice_remark';
const { t } = useI18n();
const configStore = useConfigStore();
const sortByRecentUse = computed(() => !!configStore.appConfig?.config?.order?.sort_remarks_by_recent_use);
const invoiceLineRemarkStore = defineStorageStore<RemarkItem>('invoice_line_remark')();
const invoiceRemarkStore = defineStorageStore<RemarkItem>('invoice_remark')();
const stores = { invoice_line_remark: invoiceLineRemarkStore, invoice_remark: invoiceRemarkStore };
const $emit = defineEmits(["change", "update:modelValue", "close", "confirm"]);
const $props = defineProps<{ modelValue?: boolean; data?: string; model?: ModelType }>();
const model = computed(() => $props.model || 'invoice_line_remark');
const store = computed(() => stores[model.value]);

const content = ref("");
const visible = ref($props.modelValue);
const clearing = ref(false);

watch(() => $props.modelValue, newVal => {
  visible.value = !!newVal;
  if (newVal) content.value = $props.data || "";
});
watch(() => $props.data, newVal => { content.value = newVal || ""; });

// Only CJK characters need GBK validation; non-CJK (Vietnamese, ASCII, etc.) are allowed
const isCJK = (ch: string) => { const cp = ch.codePointAt(0)!; return (cp >= 0x4E00 && cp <= 0x9FFF) || (cp >= 0x3400 && cp <= 0x4DBF) || (cp >= 0xF900 && cp <= 0xFAFF) || cp >= 0x20000; };
const findNonGbkChars = (text: string): string[] => {
  const bad = new Set<string>();
  for (const ch of Array.from(text))
    if (isCJK(ch) && iconv.decode(iconv.encode(ch, 'gbk'), 'gbk') !== ch) bad.add(ch);
  return [...bad];
};

const handleClose = () => {
  $emit("change", false);
  $emit("update:modelValue", false);
  $emit("close", false);
};

// Clear note content and emit confirm with empty string
const handleClear = async () => {
  if (clearing.value) return;
  clearing.value = true;
  content.value = '';
  $emit('confirm', '');
  setTimeout(() => clearing.value = false, 500);
};

// Parse remarks: split by delimiters, normalize Chinese-space patterns
const parseRemarks = (text: string): string[] => {
  if (!text.trim()) return [];
  // Replace space around Chinese chars with comma, then split
  const normalized = text.replace(/([\u4e00-\u9fa5])\s+|\s+([\u4e00-\u9fa5])/g, '$1$2,');
  return normalized.split(/[,，;；]+/).map(r => r.trim().replace(/\s+/g, ' ')).filter(Boolean)
    .map(r => /^[a-zA-Z]/.test(r) ? r.charAt(0).toUpperCase() + r.slice(1).toLowerCase() : r);
};

const handleConfirm = async () => {
  // Validate GBK compatibility before submitting
  const nonGbkChars = findNonGbkChars(content.value);
  if (nonGbkChars.length > 0) {
    ElNotification({
      customClass: 'x-notification__error',
      title: t('common.error'),
      message: t('note.unsupported_characters', { chars: nonGbkChars.join(' ') }),
    });
    return;
  }
  const remarks = parseRemarks(content.value);
  $emit("confirm", content.value);
  // Recent-remark list is a convenience: a slow or failed storage save must not block or drop the remark
  if (remarks.length > 0) store.value.saveNames(remarks, sortByRecentUse.value).catch(e => logError('Note : Save Recent Remarks Failed', serializeError(e)));
};

const handleQuickSelect = (text: string) => {
  if (content.value) {
    content.value += ' ' + text;
  } else {
    content.value = text;
  }
};

onMounted(async () => { await store.value.getAll(); store.value.startLiveQuery(); });
onUnmounted(() => store.value.stopLiveQuery());
</script>

<template>
  <x-dialog
    v-model="visible"
    width="770px"
    :title="t('note.title')"
    :before-close="handleClose"
    @close="handleClose"
    :close-on-click-modal="false"
  >
    <template #headerRight>
      <el-button color="#E53030" :disabled="clearing" @click="handleClear">
        <el-icon size="20" color="#fff"><DeleteOutlined /></el-icon>
      </el-button>
    </template>
    <div class="flex flex-col items-center">
        <div class="w-[355px]">
            <el-input type="textarea" v-model="content" :rows="3" resize="none" />
        </div>
        <div class="quick-buttons-container my-3 flex-1">
            <NoteQuickClick :model="model" @quick-select="handleQuickSelect" />
        </div>
        <x-button type="primary" class="w-[355px] shrink-0" :debounceTime="1000" @click="handleConfirm">{{
            t("common.confirm")
        }}</x-button>
    </div>
  </x-dialog>
</template>

<style lang="scss" scoped>
.quick-buttons-container {
    width: 650px;
    max-height: 410px;
    overflow-y: auto;
    scrollbar-width: none; // Firefox
    -ms-overflow-style: none; // IE/Edge
    &::-webkit-scrollbar { display: none; } // Chrome/Safari
}
</style>
